import clsx from "clsx";
import format from "date-fns/format";
import formatISO from "date-fns/formatISO";
import parseISO from "date-fns/parseISO";
import React, { useEffect, useState } from "react";

import Comment from "@material-ui/icons/Comment";
import MoreHorizIcon from "@material-ui/icons/MoreHoriz";

import { racehorse360 } from "@tsg/1st-grpc-web-rh360";

import Loader from "components/Loader";
import { useLoggedInUser } from "components/LoggedInUserProvider";
import ScheduleDateMenu from "components/ScheduleDateMenu";
import { useRacehorse360Api } from "hooks/api";
import { useMutationWithRefetch } from "hooks/useMutationWithRefetch";
import { WORKOUT_REQUEST_GET_OPTIONS } from "pages/WorkoutsPage/Trainer/options";

import useStyles from "./styles";

export interface Props {
  isToday?: boolean;
  workoutRequestData: racehorse360.IWorkoutRequest;
  className?: string;
  onUpdate?: (
    id?: string,
    workoutRequest?: racehorse360.IWorkoutRequest
  ) => Promise<void>;
  onUpdateError?: () => void;
  horse: racehorse360.IHorse;
  isWide?: boolean;
}

const ScheduledDate = (props: Props) => {
  const {
    className,
    isToday,
    workoutRequestData,
    horse,
    isWide,
    onUpdate,
    onUpdateError
  } = props;

  const { currentUser } = useLoggedInUser();

  const classes = useStyles({
    backgroundColor: workoutRequestData?.facility?.backgroundColor,
    strokeColor: workoutRequestData?.facility?.strokeColor
  });

  const [cardEl, setCardEl] = useState<HTMLDivElement>(null);
  const date = parseISO(workoutRequestData?.date);
  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
  const [workoutRequest, setWorkoutRequest] =
    useState<racehorse360.IWorkoutRequest>(workoutRequestData);

  const { useCancelWorkoutRequest, useMoveFacilityWorkoutRequest } =
    useRacehorse360Api();

  const { mutateAsync: cancelWorkoutRequest } = useCancelWorkoutRequest();

  const { mutateAsync: moveFacilityWorkoutRequest } =
    useMoveFacilityWorkoutRequest();

  const moveFacilityWorkoutRequestTransaction = useMutationWithRefetch({
    mutation: moveFacilityWorkoutRequest,
    refetch: onUpdate,
    onError: onUpdateError
  });

  const cancelWorkoutRequestTransaction = useMutationWithRefetch({
    mutation: cancelWorkoutRequest,
    refetch: onUpdate,
    onError: onUpdateError
  });

  const isLoading = [
    moveFacilityWorkoutRequestTransaction.isLoading,
    cancelWorkoutRequestTransaction.isLoading
  ].some(Boolean);

  const facility = workoutRequest.facility;
  const facilityCode = facility?.code?.trim();

  const handleCardClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!isLoading) {
      if (!cardEl) {
        setCardEl(event.currentTarget);
      }
      setIsMenuOpen(true);
    }
  };

  const handleWorkoutChange = async (workoutData: racehorse360.IWorkout) => {
    if (workoutData.facility) {
      const updatedWorkout = await moveFacilityWorkoutRequestTransaction.run({
        id: workoutData.id,
        facilityId: workoutData.facility.id,
        getOptions: {
          select: WORKOUT_REQUEST_GET_OPTIONS
        }
      });

      setWorkoutRequest(updatedWorkout);
    } else {
      await cancelWorkoutRequestTransaction.run({ id: workoutData.id });
      setWorkoutRequest({
        date: workoutData.date
      });
    }
  };

  const handleMenuClose = () => {
    setIsMenuOpen(false);
  };

  const handleMenuOpen = () => {
    setIsMenuOpen(true);
  };

  useEffect(() => {
    setWorkoutRequest(workoutRequestData);
  }, [workoutRequestData]);

  const renderCommentIcon = () => {
    const hasComment = currentUser.isRacingOfficial
      ? workoutRequest.hasRacingSecretaryComment
      : workoutRequest.hasComment;

    if (hasComment) {
      return <Comment className={classes.commentIcon} />;
    }
  };

  return (
    <div
      className={clsx(classes.card, classes[facilityCode], className, {
        [classes.widePane]: isWide
      })}
      onClick={handleCardClick}
      data-test-element="CALENDAR_DAY"
      data-test-value={formatISO(date, { representation: "date" })}
      data-test-state={"SCHEDULED"}
    >
      {isLoading && <Loader className={classes.loader} />}
      {workoutRequest && (
        <>
          <div className={classes.cardHeader}>{facilityCode}</div>
          <div className={classes.cardContent}>
            <span
              className={clsx(classes.date, {
                [classes.squared]: isToday
              })}
            >
              {isWide ? format(date, "iii M/d") : date.getDate()}
            </span>
          </div>
          <div className={classes.cardActions}>
            <MoreHorizIcon className={classes.moreHorizIcon} />

            {renderCommentIcon()}
          </div>
        </>
      )}
      {cardEl && (
        <ScheduleDateMenu
          horse={horse}
          anchorEl={cardEl}
          workoutData={workoutRequest}
          onChange={handleWorkoutChange}
          onClose={handleMenuClose}
          onOpen={handleMenuOpen}
          isOpen={isMenuOpen}
        />
      )}
    </div>
  );
};

export default React.memo(ScheduledDate);
